import React, { useState, useCallback, useEffect } from "react";
import apiClient from "../../../../../api/apiClient";
import Loading from "../../../../../components/Loading/Loading";
import CampaignStatusBadge from "./components/CampaignStatusBadge";
import SpaceDodger from "./components/SpaceDodger";

const CampaignList = ({ onNew, onEdit, onAnalytics }) => {
    const [campaigns, setCampaigns] = useState([]);
    const [total, setTotal]         = useState(0);
    const [loading, setLoading]     = useState(true);
    const [error, setError]         = useState("");
    const [actionLoading, setActionLoading] = useState(null);

    const fetchCampaigns = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiClient.get(`/admin/crm/campaigns?limit=50`);
            setCampaigns(res.data.campaigns);
            setTotal(res.data.total);
        } catch {
            setError("Failed to load campaigns");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);
    
    const handleStart = async (e, id) => {
        e.stopPropagation();
        setActionLoading(id);
        try {
            await apiClient.post(`/admin/crm/campaigns/${id}/start`);
            await fetchCampaigns();
        } catch (err) {
            alert(err.response?.data?.error || "Failed to start campaign");
        } finally {
            setActionLoading(null);
        }
    };
    
    const handlePause = async (e, id) => {
        e.stopPropagation();
        setActionLoading(id);
        try {
            await apiClient.post(`/admin/crm/campaigns/${id}/pause`);
            await fetchCampaigns();
        } catch (err) {
            alert(err.response?.data?.error || "Failed to pause campaign");
        } finally {
            setActionLoading(null);
        }
    };

    const handleComplete = async (e, id, name) => {
        e.stopPropagation();
        if (!window.confirm(`Are you sure you want to mark "${name}" as complete? This cannot be undone.`)) return;
        setActionLoading(id);
        try {
            await apiClient.post(`/admin/crm/campaigns/${id}/complete`);
            await fetchCampaigns();
        } catch (err) {
            alert(err.response?.data?.error || "Failed to complete campaign");
        } finally {
            setActionLoading(null);
        }
    };

    const handleDelete = async (e, id, name) => {
        e.stopPropagation();
        if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
        setActionLoading(id);
        try {
            await apiClient.delete(`/admin/crm/campaigns/${id}`);
            await fetchCampaigns();
        } catch (err) {
            alert(err.response?.data?.error || "Failed to delete campaign");
        } finally {
            setActionLoading(null);
        }
    };

    const handleEdit = (e, id) => {
        e.stopPropagation();
        onEdit(id);
    };

    return (
        <div className="camp-list-root">
            <div className="camp-list-header">
                <span className="camp-list-count">{total} campaign{total !== 1 ? "s" : ""}</span>
                <button className="admin-btn admin-btn--primary" onClick={onNew}>
                    + New Campaign
                </button>
            </div>

            {error && (
                <div className="admin-form-error" style={{ marginBottom: "var(--space-3)" }}>{error}</div>
            )}

            {loading ? (
                <div className="admin-loading-wrapper"><Loading /></div>
            ) : campaigns.length === 0 ? (
                <div className="camp-list-empty" style={{ padding: "16px 0" }}>
                    <SpaceDodger />
                </div>
            ) : (
                <div className="camp-list-table-wrap">
                    <table className="camp-list-table">
                        <thead>
                            <tr>
                                <th>Campaign</th>
                                <th>Status</th>
                                <th>Audience</th>
                                <th>Sent</th>
                                <th>Open Rate</th>
                                <th>Reply Rate</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {campaigns.map((c) => (
                                <tr
                                    key={c.id}
                                    className="camp-list-row"
                                    onClick={() => onAnalytics(c.id)}
                                >
                                    <td className="camp-list-name-cell">
                                        <span className="camp-list-name">{c.name}</span>
                                        <span className="camp-list-date">
                                            {new Date(c.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                                        </span>
                                    </td>
                                    <td><CampaignStatusBadge status={c.status} /></td>
                                    <td className="camp-list-stat">{c.totalEnrolled}</td>
                                    <td className="camp-list-stat">{c.totalSent}</td>
                                    <td className="camp-list-stat">{c.openRate}%</td>
                                    <td className="camp-list-stat">{c.replyRate}%</td>
                                    <td>
                                        <div className="camp-list-actions">
                                            {(c.status === "draft" || c.status === "paused") && (
                                                <button
                                                    className="crm-action-btn crm-action-btn--start"
                                                    title={c.status === "paused" ? "Resume" : "Start"}
                                                    onClick={(e) => handleStart(e, c.id)}
                                                    disabled={actionLoading === c.id}
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                                                </button>
                                            )}
                                            {c.status === "active" && (
                                                <button
                                                    className="crm-action-btn crm-action-btn--pause"
                                                    title="Pause"
                                                    onClick={(e) => handlePause(e, c.id)}
                                                    disabled={actionLoading === c.id}
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
                                                </button>
                                            )}
                                            {c.status !== "active" && (
                                                <button
                                                    className="crm-action-btn"
                                                    title="Edit"
                                                    onClick={(e) => handleEdit(e, c.id)}
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                                </button>
                                            )}
                                            {c.status !== "completed" && (
                                                <button
                                                    className="crm-action-btn crm-action-btn--complete"
                                                    title="Mark as Complete"
                                                    onClick={(e) => handleComplete(e, c.id, c.name)}
                                                    disabled={actionLoading === c.id}
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                                </button>
                                            )}
                                            {c.status !== "active" && (
                                                <button
                                                    className="crm-action-btn crm-action-btn--delete"
                                                    title="Delete"
                                                    onClick={(e) => handleDelete(e, c.id, c.name)}
                                                    disabled={actionLoading === c.id}
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                                </button>
                                            )}
                                            <svg className="camp-list-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default CampaignList;
